import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentData(response.data);

      if (response.data.payment_status === 'completed') {
        setPaymentStatus('success');
        return;
      } else if (response.data.payment_status === 'failed') {
        setPaymentStatus('failed');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9FB] flex items-center justify-center p-6" data-testid="payment-success-page">
      <Card className="border-stone-200 rounded-sm max-w-md w-full">
        <CardContent className="pt-12 pb-12 text-center">
          {paymentStatus === 'checking' && (
            <div>
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
              <h2 className="text-2xl font-playfair font-bold text-primary mb-2">Verifying Payment</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
            </div>
          )}

          {paymentStatus === 'success' && (
            <div data-testid="payment-success-message">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-playfair font-bold text-primary mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">Your payment has been processed successfully.</p>
              {paymentData && (
                <div className="text-left space-y-2 mb-6 p-4 bg-stone-50 rounded-sm">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount:</span>
                    <span className="font-mono font-semibold">SGD {paymentData.amount}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Type:</span>
                    <span className="uppercase text-xs font-semibold">{paymentData.payment_type}</span>
                  </div>
                </div>
              )}
              <Link to="/dashboard">
                <Button className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="back-to-dashboard-button">
                  Back to Dashboard
                </Button>
              </Link>
            </div>
          )}

          {paymentStatus === 'failed' && (
            <div>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-red-600">✕</span>
              </div>
              <h2 className="text-2xl font-playfair font-bold text-primary mb-2">Payment Failed</h2>
              <p className="text-muted-foreground mb-6">There was an issue processing your payment.</p>
              <Link to="/dashboard">
                <Button className="bg-primary hover:bg-primary/90 rounded-sm">Try Again</Button>
              </Link>
            </div>
          )}

          {paymentStatus === 'timeout' && (
            <div>
              <h2 className="text-2xl font-playfair font-bold text-primary mb-2">Verification Timeout</h2>
              <p className="text-muted-foreground mb-6">We couldn't verify your payment. Please check your dashboard or contact support.</p>
              <Link to="/dashboard">
                <Button className="bg-primary hover:bg-primary/90 rounded-sm">Back to Dashboard</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;